/**
 * QR payload the Prophet Android app scans to sign in (fixed contract):
 *
 *   axilon-prophet://pair?d=<base64url-no-padding(JSON)>
 *   JSON = {"v":1,"identity":…,"api":…,"proxy":…,"web":…,"pid":…,"s":…}
 *
 * `web` is the issuing web app's own origin; the phone opens relative answer
 * links (e.g. `/plant/…`) against it.
 *
 * Every URL is an absolute origin (optionally with a path prefix) without a
 * trailing slash. An empty base means "same origin as this page" and resolves
 * to `origin`. Plain `http://` is accepted only for local/dev hosts the phone
 * can reach on a LAN; anything else must be `https://`.
 */

export const PAIRING_QR_SCHEME_PREFIX = 'axilon-prophet://pair?d='
export const PAIRING_QR_VERSION = 1

export interface PairingQrPayloadInput {
  /** Identity API base (`IDENTITY_API_URL`); '' = same origin. */
  identityUrl: string
  /** axilon-api-service base (`ONTOLOGY_API_SERVICE_URL`); '' = same origin. */
  apiServiceUrl: string
  /** Proxy base (`PROXY_BASE_URL`); '' = same origin. */
  proxyBaseUrl: string
  pairingId: string
  secret: string
  /** `window.location.origin` of the issuing page; also emitted as `web`. */
  origin: string
}

/** Decoded JSON body of the pairing QR, key order as the phone documents it. */
export interface PairingQrJson {
  v: typeof PAIRING_QR_VERSION
  identity: string
  api: string
  proxy: string
  web: string
  pid: string
  s: string
}

export class PairingPayloadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PairingPayloadError'
  }
}

const PRIVATE_IPV4 = [/^127\./, /^10\./, /^192\.168\./, /^172\.(1[6-9]|2\d|3[01])\./, /^169\.254\./]

function isLocalDevHost(hostname: string): boolean {
  const host = hostname.toLowerCase()
  if (host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.local')) return true
  if (host === '[::1]' || host === '::1') return true
  return PRIVATE_IPV4.some((pattern) => pattern.test(host))
}

/**
 * Resolves a configured base URL to an absolute URL without a trailing slash.
 * Relative bases (`''`, `/proxy`) resolve against `origin`.
 */
export function resolveAbsoluteBaseUrl(base: string, origin: string, label: string): string {
  let url: URL
  try {
    url = new URL(base.trim(), `${origin.replace(/\/+$/, '')}/`)
  } catch {
    throw new PairingPayloadError(`The ${label} URL "${base}" is not a valid URL.`)
  }
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && isLocalDevHost(url.hostname))) {
    throw new PairingPayloadError(
      `The ${label} URL must use https:// for the mobile app (got ${url.origin}).`
    )
  }
  const path = url.pathname.replace(/\/+$/, '')
  return `${url.origin}${path}`
}

function utf8Bytes(text: string): Uint8Array {
  return new TextEncoder().encode(text)
}

/** RFC 4648 §5 base64url, without `=` padding. */
export function base64UrlEncode(text: string): string {
  const bytes = utf8Bytes(text)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function buildPairingQrJson(input: PairingQrPayloadInput): PairingQrJson {
  if (!input.pairingId) throw new PairingPayloadError('The pairing id is missing.')
  if (!input.secret) throw new PairingPayloadError('The pairing secret is missing.')
  return {
    v: PAIRING_QR_VERSION,
    identity: resolveAbsoluteBaseUrl(input.identityUrl, input.origin, 'identity'),
    api: resolveAbsoluteBaseUrl(input.apiServiceUrl, input.origin, 'API service'),
    proxy: resolveAbsoluteBaseUrl(input.proxyBaseUrl, input.origin, 'proxy'),
    web: resolveAbsoluteBaseUrl('', input.origin, 'web app'),
    pid: input.pairingId,
    s: input.secret
  }
}

/** The exact text encoded into the pairing QR code. */
export function buildPairingQrPayload(input: PairingQrPayloadInput): string {
  return `${PAIRING_QR_SCHEME_PREFIX}${base64UrlEncode(JSON.stringify(buildPairingQrJson(input)))}`
}
